import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { acknowledgementService, vendorCodeConflictService } from '../../services/apiService';
import Icon from '../AppIcon';
import Button from './Button';
import NavBadge from './NavBadge';
import { cn } from '../../utils/cn';
import amberLogo from '../../../assets/images/amber-logo.png';

const Sidebar = ({ isMobileOpen, setIsMobileOpen, isCollapsed, setIsCollapsed }) => {
  const location = useLocation();
  const { user, isAdmin, isCoordinator } = useAuth();
  const [conflictedCount, setConflictedCount] = useState(0);
  const [codeConflictCount, setCodeConflictCount] = useState(0);

  // Close sidebar on route change on mobile
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setIsMobileOpen(false);
    }
  }, [location.pathname, setIsMobileOpen]);

  // Fetch the sidebar count badges. Both badges share one cadence/trigger so they stay in
  // step: on mount, every 30s, and whenever the route changes (e.g. after resolving on the
  // Conflicting Vendors / Code Conflicts screens or running an ERP sync, then navigating).
  useEffect(() => {
    const fetchBadgeCounts = async () => {
      // Only fetch if user is admin or coordinator
      if (!isAdmin && !isCoordinator) {
        setConflictedCount(0);
        setCodeConflictCount(0);
        return;
      }

      // Conflicting Vendors — pending conflicted acknowledgements.
      try {
        const response = await acknowledgementService.getConflicted(1, 1, '');
        setConflictedCount(response?.meta?.total || 0);
      } catch (err) {
        // Silently fail - render no badge rather than a stale/broken one.
        console.error('Failed to fetch conflicted count:', err);
        setConflictedCount(0);
      }

      // Code Conflicts — companies with a pending code conflict or a duplicate row.
      try {
        const response = await vendorCodeConflictService.getCount();
        setCodeConflictCount(response?.data?.total || 0);
      } catch (err) {
        console.error('Failed to fetch code-conflict count:', err);
        setCodeConflictCount(0);
      }
    };

    fetchBadgeCounts();
    // Refresh both counts every 30 seconds
    const interval = setInterval(fetchBadgeCounts, 30000);
    return () => clearInterval(interval);
  }, [isAdmin, isCoordinator, location.pathname]); // Refresh when navigating between screens

  // Navigation items with role-based access
  const navigationItems = useMemo(() => {
    // Platform (API Management) accounts are a distinct, console-only role: they see ONLY the
    // Platform Admin group — no client-facing sections at all. Returned early so the base-role
    // client groups below are never built or shown for these accounts.
    if (user?.isPlatformSuperAdmin) {
      return [
        {
          section: 'Platform Admin',
          superAdmin: true,
          items: [
            { label: 'API Consumers', path: '/platform-admin/consumers', icon: 'Boxes' },
            { label: 'API Definitions', path: '/platform-admin/api-definitions', icon: 'KeyRound' },
            { label: 'Usage & Billing', path: '/platform-admin/usage', icon: 'BarChart3' },
            { label: 'Audit Logs', path: '/platform-admin/audit-logs', icon: 'ScrollText' },
          ],
        },
      ];
    }

    const items = [
      {
        section: 'Overview',
        items: [
          { label: 'Dashboard', path: '/dashboard-overview', icon: 'LayoutDashboard', roles: ['ADMIN', 'COORDINATOR'] },
          { label: 'MIS Report', path: '/mis-report', icon: 'FileBarChart', roles: ['ADMIN', 'COORDINATOR'] },
        ]
      },
      {
        section: 'Vendor Management',
        items: [
          { label: 'Vendor Master', path: '/vendor-master-list', icon: 'Building2', roles: ['ADMIN', 'COORDINATOR'] },
          { label: 'Approvals', path: '/vendor-approval-workflow', icon: 'CheckCircle', roles: ['ADMIN', 'COORDINATOR'] },
          { label: 'Campaigning Tool', path: '/campaigning-tool', icon: 'Megaphone', roles: ['ADMIN'] },
          // Coordinators read self-regularization runs (view only); the rest of the tool stays admin-only.
          { label: 'Vendor Verification', path: '/campaigning-tool/regularization/self', icon: 'ShieldCheck', roles: ['COORDINATOR'] },
          { label: 'Conflicting Vendors', path: '/conflicting-vendors', icon: 'AlertTriangle', roles: ['ADMIN', 'COORDINATOR'], badge: conflictedCount > 0 ? conflictedCount : null },
          { label: 'Code Conflicts', path: '/vendor-code-conflicts', icon: 'Hash', roles: ['ADMIN', 'COORDINATOR'], badge: codeConflictCount > 0 ? codeConflictCount : null },
          { label: 'Requests', path: '/supplier-requests', icon: 'FileText', roles: ['ADMIN', 'COORDINATOR', 'USER'] },
        ]
      },
      {
        section: 'Administration',
        items: [
          { label: 'User Management', path: '/user-management', icon: 'Users', roles: ['ADMIN'] },
          { label: 'Invite Management', path: '/invite-management', icon: 'Mail', roles: ['ADMIN', 'COORDINATOR'] },
          // Excel Import is hidden from the nav on request — the route and page
          // remain live at /vendor-excel-import for anyone with the direct link.
        ]
      }
    ];

    // Filter based on user role (client-facing accounts only; platform accounts returned early above)
    const userRole = user?.role?.toUpperCase();
    return items.map(group => ({
      ...group,
      items: group.items.filter(item => !item.roles || item.roles.includes(userRole))
    })).filter(group => group.items.length > 0);
  }, [user?.role, user?.isPlatformSuperAdmin, conflictedCount, codeConflictCount]);

  const isActivePath = (path) => location.pathname === path;

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300",
          isMobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setIsMobileOpen(false)}
      />

      {/* Sidebar Container */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-screen flex flex-col bg-[rgba(250,249,244,0.85)] text-cos-ink-secondary backdrop-blur-[18px] transition-all duration-300 ease-in-out shadow-cos-glass-md border-r border-cos-line-subtle",
          isCollapsed ? "w-16" : "w-64",
          isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Brand Header — centered logo + full software name */}
        <div className="relative flex-shrink-0 border-b border-cos-line-subtle bg-white/40">
          {!isCollapsed ? (
            <Link to="/dashboard-overview" className="flex flex-col items-center gap-1.5 px-4 py-4 text-center">
              <img src={amberLogo} alt="Amber Logo" className="h-9 w-auto object-contain" />
              <span className="text-[11.5px] font-bold leading-tight tracking-tight text-cos-ink [text-wrap:balance]">
                Amber Supplier Onboarding System
              </span>
            </Link>
          ) : (
            <Link to="/dashboard-overview" className="flex items-center justify-center py-4">
              <img src={amberLogo} alt="Amber Logo" className="h-8 w-auto object-contain max-w-[2.25rem]" />
            </Link>
          )}

          {/* Sidebar pin / collapse handle — toggles the sidebar (content shifts dynamically) */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? 'Pin sidebar open' : 'Collapse sidebar'}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="hidden lg:flex items-center justify-center w-7 h-7 rounded-lg hover:bg-cos-surface-hover text-cos-ink-muted hover:text-cos-teal-deep transition-colors absolute -right-3.5 top-1/2 -translate-y-1/2 border border-cos-line bg-white shadow-cos-glass-sm"
          >
            <Icon name={isCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={15} />
          </button>
        </div>

        {/* Navigation Content */}
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto py-4 custom-scrollbar">
          <nav className="flex-1 space-y-6 px-3">
            {navigationItems.map((group, idx) => (
              <div key={idx}>
                {!isCollapsed && group.section && (
                  <h3 className={cn(
                    "text-xs font-semibold uppercase tracking-wider mb-2 px-3 flex items-center gap-1.5",
                    group.superAdmin ? "text-cos-teal-deep" : "text-cos-ink-muted"
                  )}>
                    {group.superAdmin && <Icon name="ShieldCheck" size={12} />}
                    {group.section}
                  </h3>
                )}
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      title={isCollapsed ? item.label : ''}
                      className={cn(
                        "flex items-center px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 group relative",
                        isActivePath(item.path)
                          ? "bg-cos-teal text-white shadow-cos-teal-glow-lg"
                          : "text-cos-ink-secondary hover:text-cos-ink hover:bg-cos-surface-hover"
                      )}
                    >
                      <Icon
                        name={item.icon}
                        size={20}
                        className={cn(
                          "flex-shrink-0 transition-colors",
                          isActivePath(item.path) ? "text-white" : "text-cos-ink-muted group-hover:text-cos-teal-deep"
                        )}
                      />

                      {!isCollapsed && (
                        <span className="ml-3 truncate">{item.label}</span>
                      )}

                      {/* Count badge — shared by Conflicting Vendors and Code Conflicts */}
                      <NavBadge count={item.badge} isCollapsed={isCollapsed} label={item.label} />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* User Info & Logout */}
          <div className="mt-auto px-3 border-t border-cos-line-subtle pt-4">
            {!isCollapsed && user && (
              <div className="flex items-center px-3 py-2 mb-2">
                <div className="h-8 w-8 rounded-full bg-cos-beige-soft flex items-center justify-center text-cos-beige-ink text-sm font-medium">
                  {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                </div>
                <div className="ml-3 overflow-hidden">
                  <p className="text-sm font-medium text-cos-ink truncate">{user.name || 'User'}</p>
                  <p className="text-xs text-cos-ink-muted truncate">{user.role}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
